"use client";

import { createContext, type ReactNode, useContext } from "react";
import {
  type AnalysisTargetOptionsState,
  useAnalysisTargetOptions,
} from "./analysis-target-options";

type AnalysisTargetOptionsContextValue = AnalysisTargetOptionsState;

const AnalysisTargetOptionsContext =
  createContext<AnalysisTargetOptionsContextValue | null>(null);

type AnalysisTargetOptionsProviderProps = {
  children: ReactNode;
  workspaceId: string;
  shouldFetchOptions?: boolean;
};

/**
 * project/view 候補 query の owner を workspace layout 直下に固定する。
 *
 * urql v4 の `useQuery` は request/dep 変化時に hook ごとの local state を持つため、
 * chatId 遷移中に old/new `ChatThread` が並行描画されると render-phase update warning を
 * 誘発しやすい。候補一覧は chatId 非依存なので、スレッド単位ではなく workspace 単位で
 * 一度だけ購読する。
 */
export function AnalysisTargetOptionsProvider({
  children,
  workspaceId,
  shouldFetchOptions = true,
}: AnalysisTargetOptionsProviderProps) {
  const optionsState = useAnalysisTargetOptions(
    shouldFetchOptions ? workspaceId : "",
  );

  return (
    <AnalysisTargetOptionsContext.Provider value={optionsState}>
      {children}
    </AnalysisTargetOptionsContext.Provider>
  );
}

/**
 * layout 直下で共有している analysis target 候補 state を取り出す。
 *
 * `ChatThread` ごとに query owner を増やさず、route transition 中も同一 snapshot を
 * 参照させるための hook。
 */
export function useSharedAnalysisTargetOptions() {
  const context = useContext(AnalysisTargetOptionsContext);
  if (!context) {
    throw new Error(
      "useSharedAnalysisTargetOptions must be used within AnalysisTargetOptionsProvider",
    );
  }
  return context;
}
